import { userRoles } from "app/publicDictionaryValues";
import { GetProcedureListFilterNewResponseType, GetProcedureListNewResponseType, SortItems } from "service/procedureList/types";
import tableFilterMap, { TableFilterMapKeys } from "./procedureListSections/tableFilterMap";
import { isArray } from "lodash-es";

const handleUserRole = (roleModel: { roleID: string; roleName: string }[]) => {
  if (roleModel?.length === undefined || roleModel?.length === 0) {
    return false;
  }
  return roleModel.some((x) => x.roleID === userRoles.OpExAndTOO || x.roleID === userRoles.Opex);
};

const getInitTableFilterData = () => ({
  accountManagers: [],
  accountSupervisors: [],
  accountTeamLeaders: [],
  taskLabelNames: [],
  procedureFolderStatus: [],
  isHighRegulated: [],
  isTemporaryTask: [],
  isSharedTask: [],
  sharedTasks: [],
  procedureStatus: [],
  isPmpExcluded: [],
  procedureSaveInCategory: [],
  primaryProcedureOwner: [],
  procedureOwner: [],
  taskIds: [],
});

const getInitTablePayloadData = () => ({
  taskLabelName: "",
  taskId: "",
  accountStatus: 1,
  taskStatus: 1,
  isHighlyRegulated: null,
  isTemporaryTask: null,
  isSharedTask: null,
  isPmpExcluded: null,
  accountIds: [],
  taskGroupIds: [],
  categoryIds: [],
  amIds: [],
  asIds: [],
  tlIds: [],
  taskLabelNames: [],
  taskIds: [],
  procedureFolderStatusIds: [],
  procedureStatusIds: [],
  procedureSaveInCategoryIds: [],
  ppoIds: [],
  poIds: [],
  orderByAsc: false,
  orderBy: null,
});

const convertedTasks = (data: GetProcedureListNewResponseType["newData"]) => {
  const MAX_PAD = 10_000;
  const padSpacesWithNbsp = (value: string) => {
    const len = value.length;
    const start = Math.min(len - value.trimStart().length, MAX_PAD);
    const end = Math.min(len - value.trimEnd().length, MAX_PAD);
    return "\u00A0".repeat(start) + value.trim() + "\u00A0".repeat(end);
  };

  return data.map((item) => {
    const { taskStatus, highRegulated, temporaryTask, isSharedTask, pmpExcluded, ...rest } = item;

    const replacedRest = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => {
        if (typeof value === "string") {
          return [key, padSpacesWithNbsp(value)];
        }
        return [key, value];
      })
    );

    return {
      ...replacedRest,
      taskStatus: taskStatus ? "Active" : "Inactive",
      highRegulated: highRegulated ? "Yes" : "No",
      temporaryTask: temporaryTask ? "Yes" : "No",
      isSharedTask: isSharedTask ? "Yes" : "No",
      pmpExcluded: pmpExcluded ? "Yes" : "No",
    };
  });
};

const getFilterResult = (filters: Record<TableFilterMapKeys, string[] | number[] | null>, tableFilterDataNew: GetProcedureListFilterNewResponseType): SortItems | null => {
  let result = {};

  const booleanFieldMap: Record<
    Extract<TableFilterMapKeys, "highRegulated" | "pmpExcluded" | "temporaryTask" | "isSharedTask">,
    Extract<keyof GetProcedureListFilterNewResponseType, "isHighRegulated" | "isPmpExcluded" | "isTemporaryTask" | "isSharedTask">
  > = {
    highRegulated: "isHighRegulated",
    pmpExcluded: "isPmpExcluded",
    temporaryTask: "isTemporaryTask",
    isSharedTask: "isSharedTask",
  };
  for (const [filterKey, sortKey] of Object.entries(booleanFieldMap) as [keyof typeof booleanFieldMap, (typeof booleanFieldMap)[keyof typeof booleanFieldMap]][]) {
    const filterVal = filters[filterKey];
    const tableVal = tableFilterDataNew[sortKey];

    if (isArray(filterVal) && (tableVal.length > filterVal.length || (tableVal.length === 1 && filterVal.length === 1))) {
      result = {
        ...result,
        [tableFilterMap.get(filterKey)!]: filterVal[0],
      };
    } else {
      result = {
        ...result,
        [tableFilterMap.get(filterKey)!]: null,
      };
    }
  }

  if (isArray(filters.sharedTaskNames) || isArray(filters.sharedTaskIds) || isArray(filters.taskId)) {
    const arr1 = (filters.sharedTaskNames ?? []) as number[];
    const arr2 = (filters.sharedTaskIds ?? []) as number[];
    const arr3 = (filters.taskId ?? []) as number[];
    const allArrays = [arr1, arr2, arr3].filter((arr) => arr.length > 0);
    if (allArrays.length === 0) {
      result = { ...result, taskIds: [] };
    } else if (allArrays.length === 1) {
      if (arr1.length > 0) {
        result = { ...result, taskIds: arr1.length === tableFilterDataNew.sharedTasks.length ? [] : arr1 };
      } else if (arr2.length > 0) {
        result = { ...result, taskIds: arr2.length === tableFilterDataNew.sharedTasks.length ? [] : arr2 };
      } else if (arr3.length > 0) {
        result = { ...result, taskIds: arr3.length === tableFilterDataNew.taskIds.length ? [] : arr3 };
      }
    } else if (allArrays.length > 1) {
      const parsed = allArrays.map((arr) => arr.flatMap((item) => item.toString().split(",").map(Number)));
      const intersection = parsed.reduce((a, b) => a.filter((x) => b.includes(x)), parsed[0]);
      if (intersection.length === 0) {
        // will no result, directly return null
        return null;
      }
      result = { ...result, taskIds: intersection };
    }
  } else {
    result = { ...result, taskIds: [] };
  }
  (result as SortItems).taskIds = (result as SortItems).taskIds.flatMap((item) => item.toString().split(",").map(Number));

  const otherFieldMap: Record<
    Extract<
      TableFilterMapKeys,
      | "accountManager"
      | "accountSupervisor"
      | "accountTeamLeader"
      | "taskLabelName"
      | "procedureFolderStatus"
      | "procedureStatus"
      | "procedureSaveInCategory"
      | "primaryProcedureOwner"
      | "procedureOwnerName"
    >,
    Extract<
      keyof GetProcedureListFilterNewResponseType,
      | "accountManagers"
      | "accountSupervisors"
      | "accountTeamLeaders"
      | "taskLabelNames"
      | "procedureFolderStatus"
      | "procedureStatus"
      | "procedureSaveInCategory"
      | "primaryProcedureOwner"
      | "procedureOwner"
    >
  > = {
    accountManager: "accountManagers",
    accountSupervisor: "accountSupervisors",
    accountTeamLeader: "accountTeamLeaders",
    taskLabelName: "taskLabelNames",
    procedureFolderStatus: "procedureFolderStatus",
    procedureStatus: "procedureStatus",
    procedureSaveInCategory: "procedureSaveInCategory",
    primaryProcedureOwner: "primaryProcedureOwner",
    procedureOwnerName: "procedureOwner",
  };
  for (const [filterKey, sortKey] of Object.entries(otherFieldMap) as [keyof typeof otherFieldMap, (typeof otherFieldMap)[keyof typeof otherFieldMap]][]) {
    const filterVal = filters[filterKey];
    const tableVal = tableFilterDataNew[sortKey];

    if (isArray(filterVal) && tableVal.length > filterVal.length) {
      result = {
        ...result,
        [tableFilterMap.get(filterKey)!]: filterVal,
      };
    } else {
      result = {
        ...result,
        [tableFilterMap.get(filterKey)!]: [],
      };
    }
  }

  return result as SortItems;
};

const createFakeProgressGenerator = (max = 99, speed = 0.5) => {
  let progress = 0;
  const getFakeProgress = () => {
    if (progress < max) {
      const delta = (max - progress) * (Math.random() * speed);
      progress = Math.min(progress + delta, max);
    }
    return Math.floor(progress);
  };
  getFakeProgress.reset = () => {
    progress = 0;
  };
  return getFakeProgress;
};

export { handleUserRole, getInitTablePayloadData, getInitTableFilterData, convertedTasks, getFilterResult, createFakeProgressGenerator };
